<template>
  <div>
    <div v-if="!isSubmitted">
      <section
        data-tariff
        class="c-registration-step"
        ref="tariffRegistrationStepHeadingRef"
      >
        <RegistrationStepHeading
          :headline="`1. ${$t('dbc-label-your-tariff')}`"
          :showChangeButton="isTariffSelected || isActionCodeSubmitted"
          @onChange="changeTariff"
        />
        <div class="c-registration-step__content">
          <RegistrationTariff
            :tariffId="tariffId"
            :isActionCodeAllowed="isActionCodeAllowed"
            @selectTariff="selectTariff"
            @submitActionCode="(actionCode: string) => tariffId = actionCode"
            ref="tariffComponentRef"
          />
        </div>
      </section>

      <section
        ref="personalDataRef"
        class="c-registration-step"
        :class="{ 'c-registration-step--inactive': !isTariffSelected }"
      >
        <RegistrationStepHeading
          :headline="`2. ${$t('dbc-headline-your-data')}`"
          :showChangeButton="isPersonalDataSubmitted"
          @onChange="changePersonalData"
        />
        <div class="c-registration-step__content">
          <DbcBanner
            v-if="hasNexiTimeoutError"
            class="w-[calc(50%-theme(spacing.4))] max-tablet:w-[calc(75%-theme(spacing.4))] max-mobile:w-auto mx-4"
            severity="attention"
            :hasBorder="true"
            :message="
              $t(`dbc-error-nexi-timeout${isWebview() ? '-webview' : ''}`)
            "
          />
          <RegistrationForm
            v-if="selectedTariff"
            :tariffParameters="selectedTariff.inputParameters"
            :mandatoryFields="selectedTariff.registrationMandatoryFields"
            :isSubmitted="isPersonalDataSubmitted"
            :leistungselementid="leistungselementid"
            :tariffId="tariffId"
            @onSubmit="submitPersonalData"
          />
        </div>
      </section>

      <section
        ref="paymentDataRef"
        class="c-registration-step"
        :class="{ 'c-registration-step--inactive': !isPersonalDataSubmitted }"
      >
        <RegistrationStepHeading
          :headline="`3. ${$t('dbc-headline-payment')}`"
          :showChangeButton="
            (isPaymentDataSubmitted || isPaymentDataEditable) &&
            selectedTariff?.paymentNeeded
          "
          @onChange="changePaymentData"
        />
        <div
          class="c-registration-step__content"
          v-if="selectedTariff && Object.keys(selectedTariff).length !== 0"
        >
          <RegistrationPayment
            ref="paymentComponentRef"
            v-if="selectedTariff.paymentNeeded"
            :paymentMethods="selectedTariff.paymentMethods"
            :paymentSectionRef="paymentDataRef"
            @submitPaymentData="submitPaymentData"
            @isEditable="(isEditable: boolean) => (isPaymentDataEditable = isEditable)"
            @onNexiError="onNexiError"
          />
        </div>
        <div v-if="!selectedTariff?.paymentNeeded">
          {{ $t('dbc-text-no-payment-needed') }}
        </div>
      </section>

      <section
        class="c-registration-step"
        :class="{ 'c-registration-step--inactive': !isPaymentDataSubmitted }"
      >
        <RegistrationStepHeading />
        <div class="c-registration-step__content">
          <div class="c-registration__legal-notice">
            {{ $t('dbc-registration-legal-note') }}
          </div>

          <input
            class="dbc-gc-form__checkbox"
            type="checkbox"
            name="termsAndConditions"
            id="termsAndConditions"
            v-model="termsAndConditionsChecked"
            tabindex="0"
          />
          <label for="termsAndConditions" class="dbc-gc-form__label--checkbox">
            {{ $t('dbc-registration-legal-agb1') }}
            <NuxtLink
              :to="termsOfServiceUrl"
              target="_blank"
              class="c-registration__link"
              >{{ $t('dbc-registration-legal-agb') }}</NuxtLink
            >
            {{ $t('dbc-registration-legal-agb2') }}
            <NuxtLink
              :to="privacyUrl"
              target="_blank"
              class="c-registration__link"
              >{{ $t('dbc-registration-legal-privacy') }}</NuxtLink
            >
            <span v-if="locale === 'en'">.</span>
            <span v-else> {{ ` ${$t('dbc-registration-legal-agb3')}.` }}</span>
          </label>
        </div>
      </section>

      <section
        class="c-registration-step"
        :class="{
          'c-registration-step--inactive':
            !isTariffSelected ||
            !isPersonalDataSubmitted ||
            !isPaymentDataSubmitted ||
            !termsAndConditionsChecked,
        }"
      >
        <div class="c-registration-step__content">
          <button
            @click="submitRegistration"
            class="dbc-gc-btn dbc-gc-btn--primary c-registration-button c-registration-button--primary"
            :class="{
              'c-registration-button--loading': isLoading,
            }"
            :disabled="isLoading"
          >
            {{ $t('dbc-btn-complete-registration') }}
          </button>
          <div
            class="c-registration__error dbc-gt-text"
            v-if="registrationError"
            v-html="registrationError"
          ></div>
          <div
            class="c-registration__error-timeout"
            v-html="timeoutError"
            @click="isSubmitted = true"
          ></div>
        </div>
      </section>
    </div>
    <RegistrationActivation
      v-if="isSubmitted && isDACH"
      :verificationMedium="selectedTariff?.verificationMedium"
      :personalData="personalData"
      :tariffName="selectedTariff?.name"
    />
    <RegistrationActivationManually v-if="isSubmitted && !isDACH" />
  </div>
</template>

<script setup lang="ts">
defineProps<{
  termsOfServiceUrl: string
  privacyUrl: string
}>()

const route = useRoute()
const runtimeConfig = useRuntimeConfig()
const client = runtimeConfig.public.client
const tariffId = ref(
  route.query.actioncode
    ? (route.query.actioncode as string)
    : client.toUpperCase()
)
const tariffRegistrationStepHeadingRef = ref<HTMLElement | null>(null)
const leistungselementid = ref('')
const bahnBonusCookie = useCookie<BahnBonusCookie>('dbc-nc', { maxAge: 86400 })
if (bahnBonusCookie.value) {
  leistungselementid.value = bahnBonusCookie.value.leistungselementid
  tariffId.value = bahnBonusCookie.value.actioncode
} else if (route.query.actioncode && route.query.leistungselementid) {
  leistungselementid.value = route.query.leistungselementid as string
  bahnBonusCookie.value = {
    leistungselementid: leistungselementid.value,
    actioncode: route.query.actioncode as string,
  }
}
const isActionCodeAllowed = ref(
  !route.query.actioncode && !bahnBonusCookie.value
)

onMounted(() => {
  const channel = new BroadcastChannel('sepa-verify-channel')
  if (route.query['sepa-verify']) {
    channel.postMessage({
      result: route.query['sepa-verify'],
      timestamp: route.query['ts'],
    })
    window.close()
  }
})

const { t, locale, messages } = useI18n()

const isTariffSelected = ref(false)
provide('isTariffSelected', isTariffSelected)

const isActionCodeSubmitted = ref(false)
provide('isActionCodeSubmitted', isActionCodeSubmitted)

const isDACH = useState<boolean>('isDACH', () => true)

const selectedTariff = ref<Tariff | null>(null)

const personalDataRef = ref<HTMLElement | null>(null)
const tariffComponentRef = ref<InstanceType<any> | null>(null)

async function selectTariff(tariff: Tariff) {
  if (selectedTariff.value !== tariff) {
    isPersonalDataSubmitted.value = false
  }
  selectedTariff.value = tariff
  isTariffSelected.value = true

  await nextTick()
  setTimeout(() => {
    trackEvent('Registrierung', 'Tarif')
  }, 3000)
  personalDataRef.value?.scrollIntoView(true)
}

function changeTariff() {
  isTariffSelected.value = false
  isActionCodeSubmitted.value = false
  tariffComponentRef.value.$.exposed.showAllTariffs()
}

const personalData = ref<RegistrationPersonalData>()
const isPersonalDataSubmitted = ref(false)
const paymentDataRef = ref<HTMLElement | null>(null)

async function submitPersonalData(
  registrationPersonalData: RegistrationPersonalData
) {
  isPersonalDataSubmitted.value = true
  personalData.value = registrationPersonalData
  await nextTick()
  paymentDataRef.value?.scrollIntoView(true)
  trackEvent('Registrierung', 'Persönliche Daten')
  trackFormConversion('Registrierung', 'Registrierung')
  if (!selectedTariff?.value?.paymentNeeded) {
    submitPaymentData(null)
  }
}

function changePersonalData() {
  isPersonalDataSubmitted.value = false
}

const paymentData = ref<PaymentData | null>(null)
const paymentComponentRef = ref<InstanceType<any> | null>(null)
const isPaymentDataSubmitted = ref(false)
const isPaymentDataEditable = ref(false)

function submitPaymentData(paymentResult: PaymentData | null) {
  paymentData.value = paymentResult
  isPaymentDataSubmitted.value = true
  trackEvent('Registrierung', 'Zahlungsdaten')
}

function changePaymentData() {
  isPaymentDataSubmitted.value = false
  isPaymentDataEditable.value = false
  paymentComponentRef.value?.changePaymentData()
}

const termsAndConditionsChecked = ref(false)

const isLoading = ref(false)
const isSubmitted = ref(false)
const registrationError = ref('')
const timeoutError = ref('')

async function submitRegistration() {
  isLoading.value = true
  timeoutError.value = ''
  registrationError.value = ''

  const data: any = {
    ...(personalData?.value?.birthDate && {
      birthdate: personalData?.value?.birthDate,
    }),
    ...(personalData?.value?.firstName && {
      firstName: personalData?.value?.firstName,
    }),
    ...(personalData?.value?.lastName && {
      lastName: personalData?.value?.lastName,
    }),
    ...(personalData?.value?.email && {
      mail: personalData?.value?.email.toLocaleLowerCase(),
    }),
    ...(personalData?.value?.password && {
      password: personalData?.value?.password,
    }),
    ...(personalData?.value?.phoneNumber && {
      mobile: personalData?.value?.phoneNumber,
    }),
    ...(personalData?.value?.zipCode && {
      zipCode: personalData?.value?.zipCode,
    }),
    tariff: {
      id: selectedTariff?.value?.id,
    },
    concardisResponse: paymentData.value || {},
    client: runtimeConfig.public.client,
    language: locale.value,
  }

  // use 'unpersönlich' if no title chosen
  data.gender =
    personalData?.value?.title === '' ? '144252' : personalData?.value?.title

  data.tariff.tariffGroup = tariffId.value

  if (selectedTariff?.value?.inputParameters) {
    data.tariff.inputParameters = personalData?.value?.inputParameters?.map(
      (inputParameter) => {
        return {
          value: inputParameter.value.replaceAll(' ', ''),
          id: inputParameter.id,
        }
      }
    )
  }

  const response = await fetchCustomerCreate(data)

  if (response.status !== 201) {
    await handleError(response.data)
    return
  }

  // old registration returns data = null on success
  const shouldPollRegistration = response._data.data != null
  const pollResult = shouldPollRegistration
    ? await pollRegistration(response._data.data.registrationId)
    : 'success'

  if (pollResult === 'success') {
    trackRegistrationSuccess()
  } else {
    await handleError(pollResult)
  }
}

function trackRegistrationSuccess() {
  trackEvent('Registrierung', 'erfolgreich')
  isSubmitted.value = true
  isLoading.value = false
}

async function handleError(response: any) {
  isLoading.value = false
  let error = ''

  if (response === 'timeout') {
    timeoutError.value = isDACH.value
      ? t('dbc-text-timeout-error')
      : t('dbc-text-timeout-error-non-dach')
    return
  }

  // TODO: remove after broker migration
  if (!response.code) {
    error = t('dbc-error-something-went-wrong')
    registrationError.value = error
    return
  }

  const { code, errorAttributes, message } = response

  if (code === 14009 && errorAttributes) {
    const errorMessage =
      errorAttributes[0].constraintElementName === 'mobile'
        ? t('dbc-error-registration-phone-exists')
        : t('dbc-error-registration-email-exists')
    error = await formatErrorValueExists(errorMessage, response)
  } else if (code === 14007 && errorAttributes) {
    error = formatErrorFaultyValues({
      error: { code, errorAttributes, message },
      tariffParameters: selectedTariff?.value?.inputParameters,
      locale: locale.value,
      messages: messages.value,
    })
  } else {
    error = t('dbc-error-something-went-wrong')
  }

  trackEvent('Registrierung', 'fehlerhaft')
  registrationError.value = error
}

async function pollRegistration(
  registrationId: string,
  interval = 2000,
  timeout = 120000
): Promise<'success' | any> {
  const startTime = Date.now()

  const poll = async (): Promise<'success' | any> => {
    try {
      const registrationState = await fetchRegistrationState(registrationId)

      if (
        registrationState.status !== 200 ||
        registrationState._data.data.state === 'FAILED'
      ) {
        return registrationState._data.data.error
      }

      if (registrationState._data.data.state === 'SUCCEEDED') {
        return 'success'
      }
    } catch (error) {
      return error
    }

    if (Date.now() - startTime < timeout) {
      await new Promise((resolve) => setTimeout(resolve, interval))
      return await poll()
    } else {
      return 'timeout'
    }
  }

  return await poll()
}

const hasNexiTimeoutError = ref(false)
function onNexiError() {
  hasNexiTimeoutError.value = true
  personalDataRef.value?.scrollIntoView(true)
}
</script>
